/**
 * The portal half of the embedded-application theme handshake.
 *
 * An iframe is a separate document. It does not inherit the portal's custom
 * properties, its font stack, or the `data-bs-theme` attribute on our <html>,
 * and a cross-origin child cannot read our DOM. So an embedded app has no way
 * to discover which theme the portal is in unless we tell it, and until this
 * existed we never did - a partner app rendered light inside a dark portal and
 * stayed that way.
 *
 * Two channels, because they solve different halves:
 *
 *   1. A `theme` query parameter on the URL we frame them at, so their FIRST
 *      PAINT is right. A frame that renders light and flips a moment later is
 *      worse than the portal's own theme flash was, because it happens inside
 *      an already-painted shell.
 *
 *   2. A postMessage when the user toggles while the app is open. Without it
 *      an app is correct on load and wrong from the first toggle onwards,
 *      which reads as a rendering bug rather than a missing feature.
 *
 * Deliberately pure and free of React and the DOM. The URL building and the
 * decision about an inbound message are the parts worth asserting, and the
 * component around them is thin enough to read - the same split as
 * api/src/services/appTokenClaims.js, and for the same reason.
 *
 * The partner-facing specification is docs/11-EMBEDDED-APP-STYLE-GUIDE.md §4,
 * with a reference implementation of the other side in
 * docs/embedded/vizora-embed.js. This is a contract with teams who do not read
 * our source, so a change here changes both of those.
 */

/** The portal has exactly two. Anything else is not a theme. */
const THEMES = ['light', 'dark'];

export const THEME_MESSAGE = 'vizora:theme';
export const READY_MESSAGE = 'vizora:ready';

export function isTheme(value) {
  return THEMES.includes(value);
}

/**
 * The URL to frame: the configured target, plus the token when there is one,
 * plus the theme.
 *
 * Returns the origin too, because every postMessage we subsequently send must
 * name it. Computing it here means the component never has to re-parse the URL
 * and cannot accidentally fall back to '*'.
 *
 * Throws on a target that is not a URL. The caller already has an error path
 * for a frame it cannot open, and a silent `about:blank` would be worse.
 */
export function framedUrl({ targetUrl, token, theme }) {
  const url = new URL(targetUrl);      // throws on nonsense, deliberately

  if (token) url.searchParams.set('token', token);
  if (isTheme(theme)) url.searchParams.set('theme', theme);

  return { src: url.toString(), origin: url.origin };
}

/**
 * What to do with a message arriving from a framed application.
 *
 * Returns the theme to post back, or null to ignore. A decision function
 * rather than a handler so the security rules below are assertable without a
 * DOM.
 *
 * Three checks, and all three matter:
 *
 *   - The origin must be the one we framed. `window.addEventListener('message')`
 *     fires for messages from ANY window that can reach ours, so an unchecked
 *     handler is an input any page can write to.
 *   - The source must be the frame's own contentWindow. Origin alone is not
 *     enough: a second frame on the same origin, or a popup the app opened,
 *     would pass an origin check while not being the app we are talking to.
 *   - The type must be one we know. The channel may carry more later, and
 *     silently acting on an unrecognised shape is how it grows a hole.
 *
 * Only `vizora:ready` gets a reply. It exists because the app's scripts may
 * run after we minted its URL - a module script is deferred by specification,
 * so it cannot read the query parameter before first paint - and because a
 * theme change between minting and loading would otherwise be missed.
 */
export function replyToFrameMessage(event, { frameOrigin, frameWindow, theme }) {
  if (!frameOrigin || event.origin !== frameOrigin) return null;
  if (frameWindow && event.source !== frameWindow) return null;
  if (event.data?.type !== READY_MESSAGE) return null;

  return isTheme(theme) ? theme : null;
}

/** The message body the specification defines. One shape, one place. */
export function themeMessage(theme) {
  return { type: THEME_MESSAGE, theme };
}
